import IndeterminateCheckbox from '$components/Screens/SimeraCandidatesScreen/IndeterminateCheckbox/IndeterminateCheckbox';
import { SEARCH_CANDIDATES_SHORTDATE } from '$constants/datetimes';
import { SearchCandidateOutput } from '$store/modules/candidates';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  Cell,
  Row,
} from '@tanstack/react-table';
import dayjs from 'dayjs';
import { ReactChild, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as SortArrow } from '$icons/CloseArrowColor.svg';
import { ReactComponent as CV } from '$icons/uncolored/cv.svg';
import SearchCandidateModal, {
  ModalInfoType,
} from '$components/SearchCandidates/SearchCandidateModal/SearchCandidateModal';
import { useTranslation } from 'react-i18next';

type IColumnSort = {
  column: 'id';
  sortType: 'ASC' | 'DESC';
};

const SortColumnButton = ({
  children,
  onClick,
  sort,
  columnName,
}: {
  children: ReactChild;
  onClick: () => void;
  sort: IColumnSort;
  columnName: string;
}) => {
  return (
    <button
      onClick={() => onClick()}
      type="button"
      className="flex flex-row items-center justify-center gap-2"
    >
      {children}
      {columnName === sort.column && (
        <SortArrow
          className={`w-4 h-4 ${
            sort.sortType === 'ASC' ? 'rotate-180' : 'rotate-0'
          }`}
        />
      )}
    </button>
  );
};

const SearchCandidatesTable = ({
  searchCandidateData = [],
  isLoading,
  selectedRows,
  setSelectedRows,
  pageSize,
  columnSort,
  setColumnSort,
}: // openAddToPositionModal,
{
  searchCandidateData: any;
  isLoading: boolean;
  selectedRows: any;
  setSelectedRows: any;
  pageSize: number;
  columnSort: any;
  setColumnSort: any;
  // openAddToPositionModal: () => void;
}) => {
  const [t] = useTranslation('global');

  const SearchCandidateModalRef = useRef<{
    setCandidateInfo: (info: ModalInfoType) => void;
  } | null>(null);

  const clickSortColumnHandler = useCallback(
    (columnName: string) => {
      const isOtherColumn = columnName !== columnSort.column;

      const newSort = {
        column: columnName,
        sortType:
          columnSort.sortType === 'ASC' || isOtherColumn ? 'DESC' : 'ASC',
      } as IColumnSort;
      setColumnSort(newSort);
    },
    [columnSort, setColumnSort]
  );

  const columns = useMemo<ColumnDef<SearchCandidateOutput['data'][number]>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => {
          return (
            <div>
              <IndeterminateCheckbox
                {...{
                  checked: table.getIsAllRowsSelected(),
                  indeterminate: table.getIsSomeRowsSelected(),
                  onChange: table.getToggleAllRowsSelectedHandler(),
                }}
              />
            </div>
          );
        },
        cell: ({ row }) => {
          return (
            <div>
              <IndeterminateCheckbox
                {...{
                  checked: row.getIsSelected(),
                  disabled: !row.getCanSelect(),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler(),
                }}
              />
            </div>
          );
        },
      },
      {
        header: ({ column }) => (
          <SortColumnButton
            onClick={() => clickSortColumnHandler(column.id.toLowerCase())}
            sort={columnSort}
            columnName={column.id.toLowerCase()}
          >
            <span title={`${t('SearchCandidates.Table.CandidateId')}`}>ID</span>
          </SortColumnButton>
        ),
        accessorKey: 'Id',
        cell: ({ cell }) => {
          return (
            <Link
              to={`/simera-dashboard/candidates/${cell.getValue()}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex py-4 w-full h-full hover:underline hover:text-blue-500 hover:font-bold"
            >
              {cell.getValue<number>()}
            </Link>
          );
        },
      },
      {
        header: `${t('SearchCandidates.Table.Email')}`,
        accessorKey: 'User.Email',
        id: 'Email',
        cell: ({ row }) => (
          <div className="group flex flex-row gap-4 items-center">
            <span>{row.original.User.Email}</span>
            <div className="flex flex-row gap-2 items-center">
              <button
                type="button"
                onClick={() => {
                  if (SearchCandidateModalRef.current?.setCandidateInfo)
                    SearchCandidateModalRef.current?.setCandidateInfo({
                      type: 'spk',
                      candidateId: row.original.Id,
                      name: `${row.original.User.FirstName || ''} ${
                        row.original.User.LastName || ''
                      }`,
                    });
                }}
                aria-label="View Spk"
                title="View SPK"
              >
                <i className="text-internal-main pi pi-id-card text-xl invisible group-hover:visible" />
              </button>
              {row.original.Curriculum && (
                <button
                  type="button"
                  title="View CV"
                  aria-label="View CV"
                  onClick={() => {
                    if (SearchCandidateModalRef.current)
                      SearchCandidateModalRef.current?.setCandidateInfo({
                        type: 'cv',
                        candidateId: row.original.Id,
                        name: `${row.original.User.FirstName || ''} ${
                          row.original.User.LastName || ''
                        }`,
                      });
                  }}
                >
                  <CV className="fill-internal-main w-6 h-6 invisible group-hover:visible" />
                </button>
              )}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'CreateDate',
        header: () => (
          <div className="text-left">
            <div>{t('SearchCandidates.Table.CreateDate')}</div>
            <div className="text-xs">({SEARCH_CANDIDATES_SHORTDATE})</div>
          </div>
        ),
        cell: ({ row }) => {
          return (
            <div>{`${dayjs(row.original.CreateTime).format(
              SEARCH_CANDIDATES_SHORTDATE
            )}`}</div>
          );
        },
      },
      {
        accessorKey: 'LastLogin',
        header: () => (
          <div>
            <div>{t('SearchCandidates.Table.LastLogin')}</div>
            <div className="text-xs">({SEARCH_CANDIDATES_SHORTDATE})</div>
          </div>
        ),
        cell: ({ row }) => {
          return (
            <div>
              {row.original.LastLogin &&
                `${dayjs(row.original.LastLogin).format(
                  SEARCH_CANDIDATES_SHORTDATE
                )}`}
            </div>
          );
        },
      },
      {
        accessorKey: 'Country.Name',
        header: `${t('SearchCandidates.Table.Country')}`,
        id: 'country',
      },
      {
        header: `${t('SearchCandidates.Table.LastJob')}`,
        id: 'lastJob',
        cell: ({ row }) => {
          return (
            <div>
              {row.original.CandidateWorkExperience?.[0]?.JobPosition?.title ||
                ''}
            </div>
          );
        },
      },
      {
        header: `${t('SearchCandidates.Table.Salary')}`,
        cell: ({ row }) => {
          const { SalaryLowerBound, SalaryUpperBound } =
            row.original?.CandidateJobInterest || {};
          if (!SalaryLowerBound && !SalaryUpperBound) return null;
          return (
            <div>{`${SalaryLowerBound || 'N/A'} - ${
              SalaryUpperBound || 'N/A'
            }`}</div>
          );
        },
      },
      {
        id: 'cv',
        header: `${t('SearchCandidates.Table.CV')}`,
        cell: ({ row }) => {
          if (row.original?.Curriculum) return '✓';
          return null;
        },
      },
      {
        id: 'video',
        header: `${t('SearchCandidates.Table.Video')}`,
        cell: ({ row }) => {
          if (row.original?.AdditionalVideos?.length > 0) return '✓';
          return null;
        },
      },
      {
        header: `${t('SearchCandidates.Table.Reference')}`,
        cell: ({ row }) => {
          if (
            row.original?.Files?.find(
              (singleFile) =>
                singleFile.FileCategory?.CategoryName?.toLowerCase() ===
                'reference'
            )
          )
            return '✓';
          return null;
        },
      },
      {
        header: `${t('SearchCandidates.Table.Quality')}`,
        accessorKey: 'QualityStatus',
      },
      {
        header: `${t('SearchCandidates.Table.OpenToWork')}`,
        accessorKey: 'OpenToWork',
      },
      {
        accessorKey: 'OpenToWorkUpdatedOn',
        header: () => (
          <div>
            <div>{t('SearchCandidates.Table.OpenToWorkUpdatedOn')}</div>
            <div className="text-xs">({SEARCH_CANDIDATES_SHORTDATE})</div>
          </div>
        ),
        id: 'OpenToWorkUpdatedOn',
        cell: ({ row }) => {
          return (
            <div>
              {row.original.OpenToWorkUpdatedOn &&
                `${dayjs(row.original.OpenToWorkUpdatedOn).format(
                  SEARCH_CANDIDATES_SHORTDATE
                )}`}
            </div>
          );
        },
      },
    ],
    [clickSortColumnHandler, columnSort, t]
  );

  const { getHeaderGroups, getRowModel } = useReactTable({
    data: searchCandidateData,
    columns,
    state: {
      rowSelection: selectedRows,
    },
    enableRowSelection: true,
    onRowSelectionChange: setSelectedRows,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: false,
    initialState: {
      pagination: {
        pageSize,
      },
    },
    getRowId: (row) => {
      return `${row.Id}`;
    },
  });

  const handleSelectRow = (candidateId: number) => {
    if (selectedRows[candidateId as keyof typeof selectedRows]) {
      setSelectedRows((prev: any) => {
        delete prev[candidateId as keyof typeof selectedRows];
        return { ...prev };
      });
    } else {
      setSelectedRows((prev: any) => ({ ...prev, [candidateId]: true }));
    }
  };

  const handleCellClick = (
    cell: Cell<SearchCandidateOutput['data'][number], unknown>,
    row: Row<SearchCandidateOutput['data'][number]>
  ) => {
    if (cell.column.id !== 'Email' && cell.column.id !== 'Id') {
      handleSelectRow(row.original.Id as number);
    }
  };

  const toggleSelectionHandler = (candidateId: number, isAddition: boolean) => {
    setSelectedRows((prev: any) => ({ ...prev, [candidateId]: isAddition }));
  };

  return (
    <div className="bg-white p-4 rounded">
      {/* Modal to view CV or SPK */}
      <SearchCandidateModal
        candidatesList={searchCandidateData}
        ref={SearchCandidateModalRef}
        selectedRows={selectedRows}
        toggleSelectionHandler={toggleSelectionHandler}
        // openAddToPositionModal={openAddToPositionModal}
      />
      <table className="w-full">
        <thead className="sticky top-0 bg-slate-100 z-50">
          {getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="text-left">
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  className={`${header.id === 'select' && 'px-2'} py-2`}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        {!isLoading && (
          <tbody className="divide-y-2">
            {getRowModel().rows.map((row) => {
              return (
                <tr
                  key={row.id}
                  className={`${
                    row.getIsSelected() && 'bg-internal-main/10'
                  } hover:bg-internal-main/30`}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td key={cell.id}>
                        <div
                          role="button"
                          tabIndex={0}
                          className={`${
                            (cell.column.id === 'select' ||
                              cell.column.id === 'cv' ||
                              cell.column.id === 'video') &&
                            'px-2'
                          } ${cell.column.id !== 'Id' && 'py-4'} ${
                            cell.column.id === 'Email' && 'cursor-auto'
                          }`}
                          onKeyDown={() => handleCellClick(cell, row)}
                          onClick={() => handleCellClick(cell, row)}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        )}
      </table>
    </div>
  );
};

export default SearchCandidatesTable;
